import { IconButton, MenuItem, Select, Switch, Tooltip } from '@material-ui/core';
import { Chip } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import i18next from 'i18next';
import { isEmpty } from 'lodash-es';
import moment from 'moment-timezone';
import React, { JSX, useState } from 'react';

import { convertECurrencyTypeToSymbol, ECurrencyType } from '~/src/dash/api/exchange-rates';
import { EPriorities, generatePriorityList } from '~/src/dash/components/filters/common';
import { createChipWihFadingBackgroundVariations } from '~/src/dash/components/model-chip';
import { ICustomRenderCellJSXProps } from '~/src/dash/components/tables/ListViewTable/common';
import { useListViewTableStore } from '~/src/dash/components/tables/ListViewTable/context';
import { TextWithBackground } from '~/src/dash/components/TextWithBackground';
import { Div, fontSizing, Text, TextInput } from '~/src/dash/publicaUI';
import { addCommasToIntegerOrFloat } from '~/src/dash/utils/basic';
import { publicaColors } from '~/src/dash/utils/colors';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { isLanguageOversized } from '~/src/locales/common';

interface ITrendProps {
  arrow: string;
  color: string;
  backgroundColor: string;
}

interface IHighlightedStatusProps {
  color: string;
  backgroundColor: string;
}

const getTrendProps = (value?: number | null): ITrendProps => {
  if (value && value > 0) {
    return {
      arrow: '▲',
      color: publicaColors.darkGreen,
      backgroundColor: publicaColors.veryLightGreen,
    };
  }
  if (value && value < 0) {
    return {
      arrow: '▼',
      color: publicaColors.lightRed,
      backgroundColor: publicaColors.veryLightRed,
    };
  }
  return {
    arrow: '',
    color: publicaColors.gray,
    backgroundColor: publicaColors.veryLightGray,
  };
};

const getHighlightedStatusProps = (value?: string): IHighlightedStatusProps => {
  if (value && value === 'Running') {
    return {
      color: publicaColors.darkGreen,
      backgroundColor: publicaColors.veryLightGreen,
    };
  }
  if (value && value === 'Pending') {
    return {
      color: publicaColors.orange,
      backgroundColor: publicaColors.veryLightYellow,
    };
  }
  return {
    color: publicaColors.gray,
    backgroundColor: publicaColors.veryLightGray,
  };
};

// ADD CUSTOM DATA CELL RENDERS HERE
// PLEASE ADD DESCRIPTIONS AND RETAIN ALPHABETICAL ORDER

// chips to show bidder lables in bidders table
export const ChipLabelsRender = (props: ICustomRenderCellJSXProps) => {
  const values: string[] = props.value as string[];
  if (!values) return;

  return values.map((ele) => {
    return <Chip label={ele} style={{ marginRight: '10px', marginTop: '5px' }} />;
  });
};

// converts currency
export const CurrencyRender = (props: ICustomRenderCellJSXProps) => {
  const { value } = props;
  const { session } = useBaseStore();
  const { isCurrencyConversionDisabled } = useListViewTableStore();
  const convertedValue = isCurrencyConversionDisabled
    ? (value as number).toFixed(2)
    : ((value as number) * session.publisher.ExchangeRate.ExchangeValueToUSD).toFixed(2);
  return (
    <Text small>
      {`${convertECurrencyTypeToSymbol(
        session.publisher.CurrencyDisplayed,
      )} ${addCommasToIntegerOrFloat(convertedValue)}`}
    </Text>
  );
};

// converts currency for channels that supports other currencies
export const CurrencyRenderWithOtherCurrencies = (props: ICustomRenderCellJSXProps) => {
  const { value } = props;
  const { session } = useBaseStore();
  const { isCurrencyConversionDisabled } = useListViewTableStore();
  let convertedValue = value as number;
  if (
    !isCurrencyConversionDisabled &&
    session.publisher.ExchangeRate.CurrencyType !== props.row.ExchangeRate.value.CurrencyType
  ) {
    if (props.row.ExchangeRate.value.CurrencyType === ECurrencyType.CURRENCY_TYPE_USD) {
      convertedValue = (value as number) * session.publisher.ExchangeRate.ExchangeValueToUSD;
    } else {
      convertedValue =
        ((value as number) / props.row.ExchangeRate.value.ExchangeValueToUSD) *
        session.publisher.ExchangeRate.ExchangeValueToUSD;
    }
  }
  return (
    <Text small>
      {`${convertECurrencyTypeToSymbol(
        session.publisher.CurrencyDisplayed,
      )} ${addCommasToIntegerOrFloat(convertedValue.toFixed(2))}`}
    </Text>
  );
};

export const MultiCurrencyRender = (props: ICustomRenderCellJSXProps): JSX.Element => {
  const { value, row } = props;
  const currencySymbol = (row?.CurrencySymbol?.value as string) || '$';
  return (
    <Text small>{`${currencySymbol} ${addCommasToIntegerOrFloat(
      (value as number).toFixed(2),
    )}`}</Text>
  );
};

// formats timestamp ex. '2023-04-15 05:10'
export const DateTimeRender = (props: ICustomRenderCellJSXProps) => {
  const { value } = props;
  let dateTime = '';
  if (value !== '') {
    dateTime = moment(value as string).format('YYYY-MM-DD HH:mm');
  }
  return <Text small>{dateTime}</Text>;
};

// use this if you want to preserve the original timezone offset
export const DateTimeRenderUnchanged = (props: ICustomRenderCellJSXProps): JSX.Element => {
  const { value } = props;
  let dateTime = '';
  if (value !== '') {
    // Parse the timestamp and preserve the original timezone offset
    const parsedMoment = moment.parseZone(value as string);
    if (parsedMoment.valueOf() > 0) {
      dateTime = parsedMoment.format('YYYY-MM-DD HH:mm');
    }
  }
  return <Text small>{dateTime}</Text>;
};

// formats timestamp ex. '2023-04-15 05:10'
export const DateRender = (props: ICustomRenderCellJSXProps): JSX.Element => {
  const { value } = props;
  let dateTime = '';
  if (value !== '') {
    dateTime = moment(value as string).format('YYYY-MM-DD');
  }
  return <Text small>{dateTime}</Text>;
};

// formats timestamp ex. '2023-04-15 05:10:59'
export const DateTimeSecondsRender = (props: ICustomRenderCellJSXProps): JSX.Element => {
  const { value } = props;
  let dateTime = '';
  if (value !== '') {
    dateTime = moment(value as string).format('YYYY-MM-DD HH:mm:ss');
  }
  return <Text small>{dateTime}</Text>;
};

// formats long strings to break with an ellipsis and add a tooltip to display full value
export const EllipsisWithTooltipRender = (props: ICustomRenderCellJSXProps) => {
  const { value } = props;
  return (
    <Tooltip title={value} placement='top-start'>
      <div>
        <Text
          small
          bold
          style={{
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          {value}
        </Text>
      </div>
    </Tooltip>
  );
};

// format an array of string or number to a list
export const ListRender = (props: ICustomRenderCellJSXProps) => {
  const { value } = props;
  return (
    <Div style={{ textAlign: 'left' }}>
      {Array.isArray(value) ? (
        value.map((v: string | number) => <Text small>{v}</Text>)
      ) : (
        <Text small>{value}</Text>
      )}
    </Div>
  );
};

// format an array of string or number to an expandable list
interface ITruncatedLabels {
  list: JSX.Element | JSX.Element[];
  truncatedList: JSX.Element | JSX.Element[];
}

export const TruncatedChipsRender = (props: ICustomRenderCellJSXProps) => {
  const style = { marginRight: '10px', marginTop: '5px' };

  const { value } = props;
  const valueStr = Array.isArray(value) ? value.join(', ') : String(value);

  if (isEmpty(valueStr)) {
    return <div>-</div>;
  }

  const truncatedList =
    valueStr.length > 15 ? (
      <Chip style={style} label={valueStr.substring(0, 15) + '...'} size='small' />
    ) : (
      <Chip style={style} label={value} size='small' />
    );

  const list = Array.isArray(value) ? (
    value.map((v: string | number) => <Chip style={style} label={String(v)} size='small' />)
  ) : (
    <Chip style={style} label={value} size='small' />
  );

  return <TruncatedLabels list={list} truncatedList={truncatedList} />;
};

const TruncatedLabels = ({ list, truncatedList }: ITruncatedLabels) => {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <Tooltip arrow title={`Click to ${isExpanded ? 'collapse' : 'expand'}`} placement='bottom'>
      <div onClick={() => setIsExpanded(!isExpanded)} style={{ textAlign: 'left' }}>
        {isExpanded ? list : truncatedList}
      </div>
    </Tooltip>
  );
};

export const TruncatedListRender = (props: ICustomRenderCellJSXProps) => {
  const { value } = props;
  const valueStr = Array.isArray(value) ? value.join(', ') : String(value);
  const truncatedList =
    valueStr.length > 15 ? (
      <Text small>{valueStr.substring(0, 15) + '...'}</Text>
    ) : (
      <Text small>{value}</Text>
    );
  const list = Array.isArray(value) ? (
    value.map((v: string | number) => <Text small>{v}</Text>)
  ) : (
    <Text small>{value}</Text>
  );
  return <TruncatedLabels list={list} truncatedList={truncatedList} />;
};

export const PercentRender = (props: ICustomRenderCellJSXProps): JSX.Element => {
  const { value } = props;
  return (
    <Div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <Text small>{`${(value as number).toFixed(2)} %`}</Text>
    </Div>
  );
};

export const PercentTrendRender = (props: ICustomRenderCellJSXProps) => {
  const { value } = props;
  const { arrow, color, backgroundColor } = getTrendProps(value as number | null);
  const updatedValue =
    value !== null ? `${arrow} ${Math.abs(value as number).toFixed(1)} %` : 'N/A';
  return (
    <Div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <Text
        extraSmall
        bold
        style={{
          textAlign: 'right',
          padding: '4px 8px',
          borderRadius: '6px',
          backgroundColor,
          color,
          maxWidth: 'auto',
        }}
      >
        {updatedValue}
      </Text>
    </Div>
  );
};

export const HighlightedStatusTextRender = (props: ICustomRenderCellJSXProps) => {
  const { value } = props;
  const { color, backgroundColor } = getHighlightedStatusProps(value as string);

  return (
    <Div
      style={{
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Text
        extraSmall
        disableSelect
        bold
        style={{
          textAlign: 'center',
          padding: '4px 8px',
          borderRadius: '6px',
          backgroundColor,
          color,
          maxWidth: 'auto',
        }}
      >
        {value}
      </Text>
    </Div>
  );
};

// IMPORTANT!!! Read Below
// in order to use this, you need to pass in a handleChangePriority in the customFunctions prop to ListViewTable
// creates priority / bidder tier dropdown
export const PrioritySelectRender = (props: ICustomRenderCellJSXProps) => {
  const { isWriteAuthorized, customFunctions } = useListViewTableStore();
  const { session } = useBaseStore();
  const value = EPriorities[props.value.toString()];
  const list = generatePriorityList(session.publisher.ID);

  return (
    <Select
      value={value}
      onChange={(e: any) => customFunctions.handleChangePriority(e.target.value, props)}
      disabled={!isWriteAuthorized}
      style={{ fontSize: fontSizing.small }}
    >
      {Object.values(list).map((item) => {
        return <MenuItem value={item.ID}>{item.title}</MenuItem>;
      })}
    </Select>
  );
};

// widen the box
// fix the $
// edit step .10
//

export const PriceTextInputRender = (props: ICustomRenderCellJSXProps) => {
  const { isWriteAuthorized, customFunctions } = useListViewTableStore();

  const value = (props.value as Number).toFixed(2);

  const id = parseInt(props.row.ID.value, 10);

  return (
    <TextInput
      isDisabled={!isWriteAuthorized}
      inputStyle={{ textAlign: 'right' }}
      type='number'
      min={0}
      initialValue={value}
      width='6rem'
      inputSize='small'
      step={0.1}
      prefix='usd'
      handleOnChange={(input) => {
        customFunctions.handleChangePriceText(id, Number(parseFloat(input).toFixed(2)));
      }}
    />
  );
};

export const SuperUserOnlySwitchRender = (props: ICustomRenderCellJSXProps) => {
  const { isActionPending, isUpdating, customFunctions } = useListViewTableStore();
  const { value } = props;
  return (
    <Switch
      checked={value as boolean}
      disabled={isActionPending || isUpdating}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        customFunctions.handleChangeSuperUserOnly(e.target.checked, props)
      }
      onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => e.stopPropagation()}
      color='secondary'
      size='small'
    />
  );
};

export const BooleanToString = (props: ICustomRenderCellJSXProps) => {
  const { isActionPending, isUpdating } = useListViewTableStore();
  const { value, row } = props;
  const { customFunctions } = useListViewTableStore();

  return (
    <Tooltip title='Remove from distribution'>
      <IconButton
        style={{ padding: '4px' }}
        aria-label='Delete'
        disabled={isActionPending || isUpdating || value === 0}
        onClick={() => {
          customFunctions.handleOpenDeleteModal(row.ID.value, row.Name.value);
        }}
      >
        <DeleteIcon />
      </IconButton>
    </Tooltip>
  );
};

export const ChipTypeAdomain = createChipWihFadingBackgroundVariations(
  '#8749ff',
  'rgba(135,73,255,0.10)',
  'rgba(135,73,255,0.01)',
  'rgba(135,73,255,0.15)',
  true,
);

export const RequestStatus = (props: ICustomRenderCellJSXProps) => {
  const { value } = props;
  return <>{value && <TextWithBackground color='purple' text='Requested' />}</>;
};

export const CustomStatus = (props: ICustomRenderCellJSXProps) => {
  const { value } = props;
  return (
    <>
      {value === 'Requested' ? (
        <TextWithBackground color='purple' text='Requested' />
      ) : (
        <TextWithBackground color='green' text='Distributed' />
      )}
    </>
  );
};

export const SimpleTranslationRender = (props: ICustomRenderCellJSXProps): JSX.Element => {
  const isOversized = isLanguageOversized(i18next.language);
  return (
    <Text small={!isOversized} extraSmall={isOversized}>
      {props.value}
    </Text>
  );
};
